import * as Yup from "yup";

const CONTACT_PHONE_PATTERN = /^[\d\s+\-().]+$/;

export const BANNER_LOGO_ALLOWED_SIZES = [
  { width: 1200, height: 200 },
  { width: 1800, height: 300 },
  { width: 2400, height: 400 },
];

export const BANNER_LOGO_MESSAGES = {
  aspectRatioHint:
    "The banner must have a 6:1 aspect ratio. Accepted sizes: 1200 x 200 px, 1800 x 300 px, and 2400 x 400 px. Images with different dimensions will not be accepted.",
  invalidDimensions:
    "Invalid image dimensions. The banner only accepts the following sizes: 1200 x 200 px, 1800 x 300 px, or 2400 x 400 px. Please choose a different image.",
  unreadableDimensions:
    "Unable to read image dimensions. Please try another file.",
};

export const getImageDimensionsFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });

export const isValidBannerDimensions = (width, height) => {
  if (!width || !height) {
    return false;
  }

  return BANNER_LOGO_ALLOWED_SIZES.some(
    (size) => size.width === width && size.height === height,
  );
};

export const normalizeContactField = (value) => {
  if (value == null) {
    return "";
  }
  return String(value).trim();
};

/** multipart/form-data: always a string; empty clears on PUT. */
export const toContactFormDataValue = (value) =>
  String(normalizeContactField(value));

export const billingSettingsInitialValues = {
  title: "",
  contact_email: "",
  contact_phone: "",
  logo: null,
  acquirer_logo: null,
  logo_action: null,
  acquirer_logo_action: null,
};

export const billingSettingsInitialValidationSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title cannot exceed 100 characters"),

  contact_email: Yup.string()
    .trim()
    .max(254, "Email cannot exceed 254 characters")
    .test(
      "valid-email",
      "Invalid email address",
      (value) => {
        if (!value || value.trim() === "") {
          return true;
        }
        return Yup.string().email().isValidSync(value.trim());
      },
    ),

  contact_phone: Yup.string()
    .trim()
    .max(30, "Phone number cannot exceed 30 characters")
    .test(
      "valid-phone",
      "Invalid phone number",
      (value) => {
        if (!value || value.trim() === "") {
          return true;
        }
        const trimmed = value.trim();
        if (trimmed.length < 7) {
          return false;
        }
        return CONTACT_PHONE_PATTERN.test(trimmed);
      },
    ),

  logo: Yup.mixed()
    .nullable()
    .test("fileType", "Logo must be an image file", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value && value.type && value.type.startsWith("image/");
    })
    .test("fileSize", "Logo size must be less than 5MB", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value && value.size <= 5 * 1024 * 1024;
    })
    .test(
      "bannerDimensions",
      BANNER_LOGO_MESSAGES.invalidDimensions,
      async (value) => {
        if (!value) return true;
        if (typeof value === "string") return true;
        try {
          const { width, height } = await getImageDimensionsFromFile(value);
          return isValidBannerDimensions(width, height);
        } catch {
          return false;
        }
      },
    ),

  acquirer_logo: Yup.mixed()
    .nullable()
    .test("fileType", "Acquirer logo must be an image file", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value && value.type && value.type.startsWith("image/");
    })
    .test("fileSize", "Acquirer logo size must be less than 5MB", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value && value.size <= 5 * 1024 * 1024;
    }),
});

export const setBillingSettingsValues = ({
  billingSettingsData,
  setFieldValue,
}) => {
  setFieldValue("title", billingSettingsData.title || "", true);
  setFieldValue(
    "contact_email",
    billingSettingsData.contact_email || "",
    true,
  );
  setFieldValue(
    "contact_phone",
    billingSettingsData.contact_phone || "",
    true,
  );
  if (billingSettingsData.logo) {
    setFieldValue("logo", billingSettingsData.logo, true);
  }
  if (billingSettingsData.acquirer_logo) {
    setFieldValue("acquirer_logo", billingSettingsData.acquirer_logo, true);
  }
  setFieldValue("logo_action", null, true);
  setFieldValue("acquirer_logo_action", null, true);
};
